/**
 * FikoYT Wall Thickness Analysis Component
 *
 * CATIA/SolidWorks/Siemens NX benzeri profesyonel kalınlık analizi.
 * ÜÇ farklı analiz modu destekler:
 *
 * PRO GPU Modu (Yeni - En Hızlı):
 * - WebGPU compute shader ile paralel ray casting
 * - Multi-direction ray cone ile hassas ölçüm
 * - Screen-space post-processing ile CT tarama kalitesi
 * - SSS (Subsurface Scattering) efekti
 * - Animasyonlu kritik bölge vurgulama
 *
 * GPU Modu (Hızlı/Yaklaşık):
 * - Multi-pass GPU ray marching (6 yön depth map)
 * - Sphere tracing / ray stepping algoritması
 * - Real-time performans
 * - Yüzeyden karşı yüzeye derinlik haritası tabanlı
 *
 * CPU Modu (Doğru - BVH Ray Casting):
 * - three-mesh-bvh ile hızlandırılmış ray casting
 * - Her vertex için normal yönünün tersine ray atılır
 * - Gerçek duvardan-duvara kalınlık ölçümü
 * - CATIA/SolidWorks kalitesinde doğruluk
 *
 * Ortak Özellikler:
 * - mm cinsinden gradient color mapping (renk skalası)
 * - Min/Max/Ortalama kalınlık tespiti
 * - Kritik bölge vurgulama
 * - Özelleştirilebilir renk skalası
 * - Histogram analizi
 * - PDF/JSON export
 */

import { ChangeDetectorRef, OnDestroy, Input, OnInit } from '@angular/core';
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import * as THREE from 'three';
import { MeshBVH, SAH } from 'three-mesh-bvh';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { FikoYTService } from '../../../../../../../fikoyt.service';

// Yeni profesyonel sistemler
import { FikoGPUThicknessAnalyzer, getGPUThicknessAnalyzer, ThicknessStatistics, GPUMode } from './gpu/FikoGPUThicknessAnalyzer';
import { FikoThicknessCompositor, FikoThicknessDataMaterial, FikoDirectColorMaterial, ColorPresets, setupThicknessMesh, setupSmoothThicknessGeometry, ThicknessVisualizationConfig } from './shader/FikoThicknessShader';

// BVH prototype patching is handled centrally by FikoRaycastBVH.patchPrototypes()
// Do NOT patch prototypes here to avoid duplicate/racing patches.

@Component({
  selector: 'fikoyt-wall-thickness',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './wall-thickness.component.html',
  styleUrls: ['./wall-thickness.component.scss']
})
export class WallThicknessComponent implements OnInit, OnDestroy {
  @Input() anaGosterim: any = null;

  subsList: Subscription[] = [];
  haritaOlaylar: Map<string, any> = new Map();

  // Analiz modu: PRO_GPU (yeni, profesyonel), GPU (hızlı, yaklaşık) veya CPU (doğru, BVH ray casting)
  analizModu: 'PRO_GPU' | 'GPU' | 'CPU' = 'PRO_GPU';

  // Yeni profesyonel sistem
  private gpuAnalyzer: FikoGPUThicknessAnalyzer | null = null;
  private thicknessCompositor: FikoThicknessCompositor | null = null;
  private thicknessDataMaterial: FikoThicknessDataMaterial | null = null;
  private directColorMaterial: FikoDirectColorMaterial | null = null;
  private animationFrameId: number | null = null;
  private lastFrameTime: number = 0;

  // Gelişmiş istatistikler
  gelismisIstatistikler: ThicknessStatistics | null = null;
  gpuModu: GPUMode = 'CPU';

  // Renk preset'i
  renkPreseti: 'ct_scan' | 'thermal' | 'rainbow' | 'industrial' | 'scientific' = 'ct_scan';

  // SSS ayarları
  sssEnabled: boolean = true;
  sssIntensity: number = 0.3;

  // Edge glow ayarları
  edgeGlowEnabled: boolean = true;

  // Post-processing modu (2-pass vs single-pass)
  // 2-pass: Bilateral blur ile ultra-smooth gradients (daha yavaş)
  // Single-pass: DirectColorMaterial ile hızlı, smootherstep gradients
  postProcessingMode: '2-pass' | 'single-pass' = 'single-pass';

  // Ana shader ve mesh yönetimi
  shaderMaterial: THREE.ShaderMaterial | null = null;
  aktifMeshler: THREE.Mesh[] = [];
  orijinalMaterials: Map<THREE.Mesh, THREE.Material> = new Map();
  orijinalGeometries: Map<THREE.Mesh, THREE.BufferGeometry> = new Map();  // Smooth geometry için orijinali sakla
  analizAktif: boolean = false;

  // CPU modu için BVH yapıları
  private bvhMeshler: Map<THREE.Mesh, MeshBVH> = new Map();
  private cpuKalinlikVerileri: Map<THREE.Mesh, Float32Array> = new Map();
  cpuIlerleme: number = 0;  // 0-100 arası ilerleme
  cpuHesaplaniyor: boolean = false;

  // GPU Depth Render Targets (6 yön için)
  private depthTargets: Map<string, THREE.WebGLRenderTarget> = new Map();
  private depthCameras: Map<string, THREE.OrthographicCamera> = new Map();
  private depthScene: THREE.Scene | null = null;

  // Kalınlık parametreleri
  kritikKalinlik: number = 1.0;   // mm - bu değerin altı kritik
  rayMarchSteps: number = 64;     // Ray marching adım sayısı
  rayMarchPrecision: number = 0.001; // Hassasiyet

  // Renk skalası (gradient) - mm cinsinden ölçü aralıkları
  // Varsayılan: Geniş aralık (1mm - 50mm) - farklı parça tiplerine uygun
  renkSkalasi: {
    kalinlik: number;  // mm cinsinden kalınlık değeri
    renk: string;
  }[] = [
    { kalinlik: 1.0, renk: '#ff0000' },   // Çok ince - Kırmızı
    { kalinlik: 5.0, renk: '#ff6600' },   // İnce - Turuncu
    { kalinlik: 10.0, renk: '#ffff00' },  // Orta-ince - Sarı
    { kalinlik: 15.0, renk: '#88ff00' },  // Orta - Açık yeşil
    { kalinlik: 20.0, renk: '#00ff00' },  // Normal - Yeşil
    { kalinlik: 25.0, renk: '#00ffaa' },  // Kalın - Turkuaz
    { kalinlik: 35.0, renk: '#00aaff' },  // Çok kalın - Açık mavi
    { kalinlik: 50.0, renk: '#0066ff' }   // Maksimum - Mavi
  ];

  // Görselleştirme ayarları
  showLegend: boolean = true;
  transparentThinAreas: boolean = false;
  highlightCritical: boolean = true;
  kritikRenk: string = '#ff0000';
  kritikParlaklik: number = 0.5;

  // Lighting ayarları
  gelismisAyarlarAcik: boolean = false;
  keyLightIntensity: number = 0.6;
  fillLightIntensity: number = 0.4;
  rimLightIntensity: number = 0.3;
  ambientLight: number = 0.3;

  // AO & Edge
  aoIntensity: number = 3.0;
  aoMax: number = 0.5;
  edgeIntensity: number = 2.0;
  edgeDarkness: number = 0.3;

  // Analiz sonuçları
  sonuclar: {
    minBulunan: number;
    maxBulunan: number;
    ortalamaKalinlik: number;
    kritikYuzde: number;
  } | null = null;

  // Depth texture boyutu
  private readonly DEPTH_TEXTURE_SIZE = 2048;

  // Pre-allocated buffers for performance optimization (zero-allocation in loops)
  private _tmpVec3A = new THREE.Vector3();
  private _tmpVec3B = new THREE.Vector3();
  private _tmpVec3C = new THREE.Vector3();
  private _tmpVec3D = new THREE.Vector3();
  private _tmpVec3E = new THREE.Vector3();
  private _tmpVec3F = new THREE.Vector3();
  private _tmpMat3 = new THREE.Matrix3();
  private _tmpMat4 = new THREE.Matrix4();
  private _tmpBox3 = new THREE.Box3();
  private _tmpColor = new THREE.Color();

  constructor(
    public readonly FikoYT: FikoYTService,
    public readonly CD: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    try { (window as any).__fikoKampanyaBildir?.('ANALIZ', 'kalinlik', 'Et Kalınlığı'); } catch { }
    this.shaderOlustur();
    this.initProGPU();
  }

  /**
   * Profesyonel GPU sistemini initialize et
   */
  private async initProGPU(): Promise<void> {
    this.gpuAnalyzer = getGPUThicknessAnalyzer();

    // GPU init'in tamamlanmasını bekle
    await this.gpuAnalyzer.waitForInit();

    this.gpuModu = this.gpuAnalyzer.getGPUMode();
    this.CD.detectChanges();
  }

  /**
   * PRO GPU analizi başlat
   */
  private async proGPUAnaliziBaslat(): Promise<void> {
    if (!this.gpuAnalyzer) {
      this.initProGPU();
    }

    this.cpuHesaplaniyor = true;
    this.cpuIlerleme = 0;
    this.CD.detectChanges();

    try {
      // KRITIK: aktifMeshler kullan, seciliMeshleriAl() değil!
      // analiziBaslat() içinde aktifMeshler zaten doldurulmuş durumda.
      // Eğer tekrar seciliMeshleriAl() çağırırsak, farklı sırada veya
      // farklı mesh'ler dönebilir ve thickness attribute'ları yanlış mesh'lere eklenir.
      if (this.aktifMeshler.length === 0) {
        this.cpuHesaplaniyor = false;
        return;
      }

      // GPU analizi yap - aktifMeshler kullan
      const result = await this.gpuAnalyzer!.analyze(this.aktifMeshler, {
        maxDistance: 1000,
        epsilon: 0.001,
        criticalThickness: this.kritikKalinlik,
        multiRayEnabled: true,
        coneAngle: 30,
        coneRayCount: 4,
        onProgress: (progress) => {
          this.cpuIlerleme = progress;
          this.CD.detectChanges();
        }
      });

      // İstatistikleri kaydet
      this.gelismisIstatistikler = result.statistics;
      this.sonuclar = {
        minBulunan: result.statistics.minThickness,
        maxBulunan: result.statistics.maxThickness,
        ortalamaKalinlik: result.statistics.avgThickness,
        kritikYuzde: result.statistics.criticalPercentage
      };

      // Mesh'lere CT-SCAN kalitesinde smooth thickness geometry uygula
      // Flow analizi gibi pürüzsüz görünüm için:
      // 1. Geometry Subdivision (her üçgen → 4 üçgen)
      // 2. Laplacian Smoothing (komşu ortalaması)
      let vertexOffset = 0;
      for (const mesh of this.aktifMeshler) {
        const geometry = mesh.geometry as THREE.BufferGeometry;
        const posAttr = geometry.getAttribute('position');
        if (!posAttr) continue;

        const vertexCount = posAttr.count;
        const meshThicknesses = result.thicknesses.slice(vertexOffset, vertexOffset + vertexCount);
        const meshSSS = result.sssFactors.slice(vertexOffset, vertexOffset + vertexCount);

        // Orijinal geometry'yi sakla (temizlik için)
        this.orijinalGeometries.set(mesh, geometry);

        // Non-indexed geometry'yi indexed'a çevir (subdivision için gerekli)
        if (!geometry.index) {
          const posAttrData = geometry.getAttribute('position');
          const vertCount = posAttrData.count;
          const indexArray = new Uint32Array(vertCount);
          for (let i = 0; i < vertCount; i++) {
            indexArray[i] = i;
          }
          geometry.setIndex(new THREE.BufferAttribute(indexArray, 1));
        }

        // CT-SCAN KALİTESİNDE SMOOTH GEOMETRY
        // subdivision: 2 level (16x üçgen), smoothing: 8 iteration
        // Flow analizi gibi pürüzsüz görünüm için yüksek değerler
        const smoothGeometry = setupSmoothThicknessGeometry(
          geometry,
          meshThicknesses,
          meshSSS,
          8,  // smoothIterations - Laplacian smoothing (daha fazla = daha smooth)
          2   // subdivisionLevels - 2 seviye = 16x üçgen (1 seviye yetmedi)
        );

        // Yeni smooth geometry'yi mesh'e ata
        mesh.geometry = smoothGeometry;

        vertexOffset += vertexCount;
      }

      // Otomatik renk skalası
      this.otomatikRenkSkalasıOlustur(result.statistics.minThickness, result.statistics.maxThickness);

      // Material'ları oluştur
      this.initCompositor();

      // DirectColorMaterial kullan - GPU-friendly, tek pass, smooth gradients
      // Fragment shader'da smootherstep ile yumuşak geçişler
      for (const mesh of this.aktifMeshler) {
        if (this.directColorMaterial) {
          mesh.material = this.directColorMaterial;
          mesh.material.needsUpdate = true;
        }
      }

      // Animasyon döngüsü başlat (postProcessingMode'a göre seçim yapılır)
      this.startRenderLoop();

      this.anaGosterim?.ciz();

    } catch (error: any) {
      // PRO GPU analysis error - silently handled
    } finally {
      this.cpuHesaplaniyor = false;
      this.cpuIlerleme = 100;
      this.CD.detectChanges();
    }
  }

  /**
   * Compositor'ı initialize et
   */
  private initCompositor(): void {
    const renderer = this.rendererAl();
    if (!renderer) return;

    // Mevcut compositor'ı temizle
    if (this.thicknessCompositor) {
      this.thicknessCompositor.dispose();
    }

    // Yeni compositor oluştur
    this.thicknessCompositor = new FikoThicknessCompositor(renderer, {
      minThickness: this.sonuclar?.minBulunan || 0.5,
      maxThickness: this.sonuclar?.maxBulunan || 20,
      criticalThickness: this.kritikKalinlik,
      highlightCritical: this.highlightCritical,
      criticalPulseSpeed: 3.0,
      criticalPulseIntensity: this.kritikParlaklik,
      sssEnabled: this.sssEnabled,
      sssIntensity: this.sssIntensity,
      aoIntensity: this.aoIntensity,
      edgeGlow: this.edgeGlowEnabled,
      colorPreset: this.renkPreseti
    });

    // Data material oluştur (post-processing için)
    this.thicknessDataMaterial = new FikoThicknessDataMaterial({
      minThickness: this.sonuclar?.minBulunan || 0.5,
      maxThickness: this.sonuclar?.maxBulunan || 20,
      criticalThickness: this.kritikKalinlik
    });

    // Doğrudan renk material oluştur (smooth gradient ile)
    // Analiz sonuçlarına göre dinamik renk skalası kullan
    const minT = this.sonuclar?.minBulunan || 0.5;
    const maxT = this.sonuclar?.maxBulunan || 20;

    this.directColorMaterial = new FikoDirectColorMaterial({
      minThickness: minT,
      maxThickness: maxT,
      criticalThickness: this.kritikKalinlik,
      highlightCritical: this.highlightCritical,
      criticalPulseSpeed: 3.0,
      criticalPulseIntensity: this.kritikParlaklik,
      sssEnabled: this.sssEnabled,
      sssIntensity: this.sssIntensity,
      colorPreset: this.renkPreseti
    });

    // Dinamik renk skalası: analiz sonuçlarına göre 8 noktalı gradient
    const range = maxT - minT;
    const dynamicThicknesses = new Float32Array([
      minT,
      minT + range * 0.14,
      minT + range * 0.28,
      minT + range * 0.42,
      minT + range * 0.57,
      minT + range * 0.71,
      minT + range * 0.85,
      maxT
    ]);

    // Preset renklerini al
    const preset = ColorPresets[this.renkPreseti] || ColorPresets.ct_scan;

    // DirectColorMaterial uniform'larını güncelle (fallback için)
    const thicknessUniform = this.directColorMaterial.uniforms.uColorThicknesses.value as Float32Array;
    for (let i = 0; i < 8; i++) {
      thicknessUniform[i] = dynamicThicknesses[i];
    }
    const colorValuesUniform = this.directColorMaterial.uniforms.uColorValues.value as THREE.Color[];
    for (let i = 0; i < 8; i++) {
      colorValuesUniform[i].copy(preset.colors[i] || new THREE.Color(0x808080));
    }
    this.directColorMaterial.uniforms.uMinThickness.value = minT;
    this.directColorMaterial.uniforms.uMaxThickness.value = maxT;
    this.directColorMaterial.needsUpdate = true;

    // Compositor'a da dinamik renk skalasını uygula (2-pass post-processing için)
    if (this.thicknessCompositor) {
      this.thicknessCompositor.setDynamicColorScale(minT, maxT);

      // Data material'ı da güncelle
      if (this.thicknessDataMaterial) {
        this.thicknessDataMaterial.update({
          minThickness: minT,
          maxThickness: maxT,
          criticalThickness: this.kritikKalinlik
        });
      }
    }

  }

  /**
   * Render döngüsünü başlat
   * postProcessingMode'a göre 2-pass veya single-pass seçilir
   */
  private startRenderLoop(): void {
    if (this.postProcessingMode === '2-pass') {
      this.start2PassRenderLoop();
    } else {
      this.startSimpleRenderLoop();
    }
  }

  /**
   * 2-Pass Post-Processing render döngüsü
   * Bilateral blur ile ultra-smooth CT-scan kalitesi
   */
  private start2PassRenderLoop(): void {
    if (this.animationFrameId !== null) return;

    const scene = this.anaGosterim?.scene as THREE.Scene;
    const camera = this.anaGosterim?.camera as THREE.Camera;
    if (!scene || !camera || !this.thicknessCompositor || !this.thicknessDataMaterial) {
      this.startSimpleRenderLoop();
      return;
    }

    let accumulatedTime = 0;

    const animate = (currentTime: number) => {
      const deltaTime = (currentTime - this.lastFrameTime) / 1000;
      this.lastFrameTime = currentTime;
      accumulatedTime += deltaTime;

      if (this.analizAktif && this.aktifMeshler.length > 0) {
        // Compositor animasyonu güncelle
        this.thicknessCompositor!.update(deltaTime);

        // Her mesh için 2-pass render
        for (const mesh of this.aktifMeshler) {
          const originalMaterial = this.orijinalMaterials.get(mesh) || mesh.material;

          this.thicknessCompositor!.render(
            scene,
            camera,
            mesh,
            this.thicknessDataMaterial!,
            originalMaterial
          );
        }
      }

      this.animationFrameId = requestAnimationFrame(animate);
    };

    this.lastFrameTime = performance.now();
    this.animationFrameId = requestAnimationFrame(animate);
  }

  /**
   * Basit render döngüsü (Single-pass - DirectColorMaterial ile)
   * Smootherstep gradients ile profesyonel görünüm
   */
  private startSimpleRenderLoop(): void {
    if (this.animationFrameId !== null) return;

    let accumulatedTime = 0;

    const animate = (currentTime: number) => {
      const deltaTime = (currentTime - this.lastFrameTime) / 1000;
      this.lastFrameTime = currentTime;
      accumulatedTime += deltaTime;

      if (this.analizAktif) {
        // Direct color material animasyonu (kritik bölge pulse)
        if (this.directColorMaterial) {
          this.directColorMaterial.update(accumulatedTime);
        }

        // Sahneyi yeniden çiz
        this.anaGosterim?.ciz();
      }

      this.animationFrameId = requestAnimationFrame(animate);
    };

    this.lastFrameTime = performance.now();
    this.animationFrameId = requestAnimationFrame(animate);
  }

  /**
   * Render döngüsünü durdur
   */
  private stopRenderLoop(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Renk preset'ini değiştir
   */
  public renkPresetiDegistir(preset: 'ct_scan' | 'thermal' | 'rainbow' | 'industrial' | 'scientific'): void {
    this.renkPreseti = preset;

    if (this.thicknessCompositor) {
      this.thicknessCompositor.setConfig({ colorPreset: preset });
    }

    // Direct color material güncelle
    if (this.directColorMaterial) {
      this.directColorMaterial.setConfig({ colorPreset: preset });
    }

    // Renk skalasını preset'e göre güncelle
    const presetData = ColorPresets[preset];
    if (presetData) {
      this.renkSkalasi = presetData.thicknesses.map((thickness, i) => ({
        kalinlik: thickness,
        renk: '#' + presetData.colors[i].getHexString()
      }));
    }

    this.shaderParametreleriniGuncelle();
    this.anaGosterim?.ciz();
    this.CD.detectChanges();
  }

  /**
   * SSS ayarlarını güncelle
   */
  public sssAyarlariGuncelle(): void {
    if (this.thicknessCompositor) {
      this.thicknessCompositor.setConfig({
        sssEnabled: this.sssEnabled,
        sssIntensity: this.sssIntensity
      });
    }

    if (this.directColorMaterial) {
      this.directColorMaterial.setConfig({
        sssEnabled: this.sssEnabled,
        sssIntensity: this.sssIntensity
      });
    }
    this.anaGosterim?.ciz();
  }

  /**
   * Edge glow ayarlarını güncelle
   */
  public edgeGlowGuncelle(): void {
    if (this.thicknessCompositor) {
      this.thicknessCompositor.setConfig({
        edgeGlow: this.edgeGlowEnabled
      });
    }
    // DirectColorMaterial'da edge glow yok (rim lighting var)
    this.anaGosterim?.ciz();
  }

  /**
   * Histogram rengi hesapla
   */
  public getHistogramColor(index: number, total: number): string {
    const t = total > 1 ? index / (total - 1) : 0;

    // CT-scan renk gradyanı (kırmızı → sarı → yeşil → mavi)
    if (t < 0.25) {
      return `rgb(255, ${Math.round(t * 4 * 100)}, 0)`;
    } else if (t < 0.5) {
      return `rgb(255, ${Math.round(100 + (t - 0.25) * 4 * 155)}, 0)`;
    } else if (t < 0.75) {
      return `rgb(${Math.round(255 - (t - 0.5) * 4 * 127)}, 255, 0)`;
    } else {
      return `rgb(${Math.round(128 - (t - 0.75) * 4 * 128)}, 255, ${Math.round((t - 0.75) * 4 * 255)})`;
    }
  }

  /**
   * Histogram tooltip metni oluştur
   */
  public getHistogramTooltip(index: number, stats: ThicknessStatistics): string {
    if (!stats || !stats.histogram || !stats.histogram[index]) return '';

    const bar = stats.histogram[index];
    return `${bar.bin.toFixed(2)}mm: ${bar.count.toFixed(1)}%`;
  }

  /**
   * JSON export
   */
  public exportJSON(): void {
    if (!this.sonuclar || !this.gelismisIstatistikler) return;

    const exportData = {
      timestamp: new Date().toISOString(),
      gpuMode: this.gpuModu,
      analysisMode: this.analizModu,
      statistics: this.gelismisIstatistikler,
      colorScale: this.renkSkalasi,
      settings: {
        criticalThickness: this.kritikKalinlik,
        sssEnabled: this.sssEnabled,
        sssIntensity: this.sssIntensity,
        edgeGlow: this.edgeGlowEnabled,
        colorPreset: this.renkPreseti
      }
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wall-thickness-analysis-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  public baslat(anaGosterim?: any) {
    this.anaGosterim = anaGosterim;

    if (!this.shaderMaterial) {
      this.shaderOlustur();
    }

    // FikoOlcumler mesh'lerini raycast'ten kapat
    if (this.anaGosterim?.sahneler?.[0]) {
      this.anaGosterim.sahneler[0].traverse((child: any) => {
        if (child instanceof THREE.Mesh &&
          (child.userData?.type === 'FikoOlcumler' || child.parent?.name === 'Ölçümler')) {
          child.userData.raycastable = false;
        }
      });
    }

    this.CD.detectChanges();
  }

  public bitir() {
    this.analiziDurdur();
    this.depthTargetsTemizle();

    // FikoOlcumler'leri geri raycast'e aç
    if (this.anaGosterim?.sahneler?.[0]) {
      this.anaGosterim.sahneler[0].traverse((child: any) => {
        if (child instanceof THREE.Mesh &&
          (child.userData?.type === 'FikoOlcumler' || child.parent?.name === 'Ölçümler')) {
          child.userData.raycastable = true;
        }
      });
    }
  }

  /**
   * Ana kalınlık analizi shader'ı
   * 6 yönden depth map alıp ray-marching ile kalınlık hesaplar
   */
  private shaderOlustur() {
    // Renk skalasını shader'a hazırla (mm değerlerini doğrudan kullan)
    const maxColors = 8;
    const colorThicknesses = new Float32Array(maxColors);  // mm cinsinden kalınlık değerleri
    const colorValues = new Float32Array(maxColors * 3);

    // Sıralı renk skalası
    const sortedSkala = [...this.renkSkalasi].sort((a, b) => a.kalinlik - b.kalinlik);

    for (let i = 0; i < maxColors; i++) {
      if (i < sortedSkala.length) {
        const item = sortedSkala[i];
        colorThicknesses[i] = item.kalinlik;  // Doğrudan mm değeri
        const color = new THREE.Color(item.renk);
        colorValues[i * 3] = color.r;
        colorValues[i * 3 + 1] = color.g;
        colorValues[i * 3 + 2] = color.b;
      } else {
        colorThicknesses[i] = 9999.0;  // Kullanılmayan slotlar için büyük değer
        colorValues[i * 3] = 0.0;
        colorValues[i * 3 + 1] = 0.0;
        colorValues[i * 3 + 2] = 1.0;
      }
    }

    this.shaderMaterial = new THREE.ShaderMaterial({
      uniforms: {
        // Depth maps (6 yön)
        depthMapPosX: { value: null },
        depthMapNegX: { value: null },
        depthMapPosY: { value: null },
        depthMapNegY: { value: null },
        depthMapPosZ: { value: null },
        depthMapNegZ: { value: null },

        // Kamera matrisleri (6 yön)
        depthVPMatrixPosX: { value: new THREE.Matrix4() },
        depthVPMatrixNegX: { value: new THREE.Matrix4() },
        depthVPMatrixPosY: { value: new THREE.Matrix4() },
        depthVPMatrixNegY: { value: new THREE.Matrix4() },
        depthVPMatrixPosZ: { value: new THREE.Matrix4() },
        depthVPMatrixNegZ: { value: new THREE.Matrix4() },

        // Kamera bilgileri
        cameraNearFar: { value: new THREE.Vector2(0.1, 1000) },
        boundingBoxMin: { value: new THREE.Vector3() },
        boundingBoxMax: { value: new THREE.Vector3() },

        // Kalınlık parametreleri (min/max renk skalasından alınır)
        minThickness: { value: sortedSkala.length > 0 ? sortedSkala[0].kalinlik : 0.5 },
        maxThickness: { value: sortedSkala.length > 0 ? sortedSkala[sortedSkala.length - 1].kalinlik : 10.0 },
        criticalThickness: { value: this.kritikKalinlik },
        rayMarchSteps: { value: this.rayMarchSteps },
        rayMarchPrecision: { value: this.rayMarchPrecision },

        // Renk skalası (mm cinsinden kalınlık değerleri)
        colorThicknesses: { value: colorThicknesses },
        colorValues: { value: colorValues },
        colorCount: { value: sortedSkala.length },

        // Görsel ayarlar
        highlightCritical: { value: this.highlightCritical },
        criticalColor: { value: new THREE.Vector3(1.0, 0.0, 0.0) },
        criticalBrightness: { value: this.kritikParlaklik },
        transparentThin: { value: this.transparentThinAreas },

        // Lighting
        keyLightPos: { value: new THREE.Vector3(0.7, 1.0, 0.5) },
        keyLightIntensity: { value: this.keyLightIntensity },
        fillLightPos: { value: new THREE.Vector3(-0.5, 0.3, 0.5) },
        fillLightIntensity: { value: this.fillLightIntensity },
        rimLightPos: { value: new THREE.Vector3(0.0, 0.0, -1.0) },
        rimLightIntensity: { value: this.rimLightIntensity },
        ambientLight: { value: this.ambientLight },

        // AO & Edge
        aoIntensity: { value: this.aoIntensity },
        aoMax: { value: this.aoMax },
        edgeIntensity: { value: this.edgeIntensity },
        edgeDarkness: { value: this.edgeDarkness }
      },
      side: THREE.DoubleSide,
      transparent: true,
      vertexShader: this.getVertexShader(),
      fragmentShader: this.getFragmentShader()
    });
  }

  private getVertexShader(): string {
    return `
      varying vec3 vNormal;
      varying vec3 vWorldNormal;
      varying vec3 vWorldPosition;
      varying vec3 vViewPosition;

      void main() {
        // View-space normal
        vNormal = normalize(normalMatrix * normal);

        // World position
        vec4 worldPosition = modelMatrix * vec4(position, 1.0);
        vWorldPosition = worldPosition.xyz;

        // World normal
        vWorldNormal = normalize(mat3(modelMatrix) * normal);

        // View space position
        vec4 viewPosition = modelViewMatrix * vec4(position, 1.0);
        vViewPosition = viewPosition.xyz;

        gl_Position = projectionMatrix * viewPosition;
      }
    `;
  }

  private getFragmentShader(): string {
    return `
      // Depth maps (6 yön)
      uniform sampler2D depthMapPosX;
      uniform sampler2D depthMapNegX;
      uniform sampler2D depthMapPosY;
      uniform sampler2D depthMapNegY;
      uniform sampler2D depthMapPosZ;
      uniform sampler2D depthMapNegZ;

      // View-Projection matrisleri
      uniform mat4 depthVPMatrixPosX;
      uniform mat4 depthVPMatrixNegX;
      uniform mat4 depthVPMatrixPosY;
      uniform mat4 depthVPMatrixNegY;
      uniform mat4 depthVPMatrixPosZ;
      uniform mat4 depthVPMatrixNegZ;

      // Kamera ve bounding box
      uniform vec2 cameraNearFar;
      uniform vec3 boundingBoxMin;
      uniform vec3 boundingBoxMax;

      // Kalınlık parametreleri
      uniform float minThickness;
      uniform float maxThickness;
      uniform float criticalThickness;
      uniform int rayMarchSteps;
      uniform float rayMarchPrecision;

      // Renk skalası (mm cinsinden kalınlık değerleri)
      uniform float colorThicknesses[8];
      uniform float colorValues[24];
      uniform int colorCount;

      // Görsel ayarlar
      uniform bool highlightCritical;
      uniform vec3 criticalColor;
      uniform float criticalBrightness;
      uniform bool transparentThin;

      // Lighting
      uniform vec3 keyLightPos;
      uniform float keyLightIntensity;
      uniform vec3 fillLightPos;
      uniform float fillLightIntensity;
      uniform vec3 rimLightPos;
      uniform float rimLightIntensity;
      uniform float ambientLight;

      // AO & Edge
      uniform float aoIntensity;
      uniform float aoMax;
      uniform float edgeIntensity;
      uniform float edgeDarkness;

      // Varyings
      varying vec3 vNormal;
      varying vec3 vWorldNormal;
      varying vec3 vWorldPosition;
      varying vec3 vViewPosition;

      // Depth unpack (RGBA -> float)
      float unpackRGBAToDepth(vec4 v) {
        return dot(v, vec4(1.0, 1.0/255.0, 1.0/65025.0, 1.0/16581375.0));
      }

      // Depth map'ten mesafe oku
      float sampleDepthDistance(sampler2D depthMap, mat4 vpMatrix, vec3 worldPos, vec3 rayDir) {
        vec4 clipPos = vpMatrix * vec4(worldPos, 1.0);
        vec3 ndc = clipPos.xyz / clipPos.w;
        vec2 uv = ndc.xy * 0.5 + 0.5;

        if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
          return -1.0;
        }

        float sampledDepth = unpackRGBAToDepth(texture2D(depthMap, uv));
        float currentDepth = ndc.z * 0.5 + 0.5;

        // Lineer mesafe hesapla
        float depthDiff = sampledDepth - currentDepth;
        float boxSize = length(boundingBoxMax - boundingBoxMin);

        return depthDiff * boxSize * 2.0;
      }

      // Normal yönüne göre en uygun depth map'i seç ve mesafe hesapla
      float calculateThicknessInDirection(vec3 worldPos, vec3 direction) {
        float minDist = 999999.0;

        // +X yönü
        if (direction.x > 0.1) {
          float d = sampleDepthDistance(depthMapNegX, depthVPMatrixNegX, worldPos, direction);
          if (d > 0.0) minDist = min(minDist, d);
        }
        // -X yönü
        if (direction.x < -0.1) {
          float d = sampleDepthDistance(depthMapPosX, depthVPMatrixPosX, worldPos, direction);
          if (d > 0.0) minDist = min(minDist, d);
        }
        // +Y yönü
        if (direction.y > 0.1) {
          float d = sampleDepthDistance(depthMapNegY, depthVPMatrixNegY, worldPos, direction);
          if (d > 0.0) minDist = min(minDist, d);
        }
        // -Y yönü
        if (direction.y < -0.1) {
          float d = sampleDepthDistance(depthMapPosY, depthVPMatrixPosY, worldPos, direction);
          if (d > 0.0) minDist = min(minDist, d);
        }
        // +Z yönü
        if (direction.z > 0.1) {
          float d = sampleDepthDistance(depthMapNegZ, depthVPMatrixNegZ, worldPos, direction);
          if (d > 0.0) minDist = min(minDist, d);
        }
        // -Z yönü
        if (direction.z < -0.1) {
          float d = sampleDepthDistance(depthMapPosZ, depthVPMatrixPosZ, worldPos, direction);
          if (d > 0.0) minDist = min(minDist, d);
        }

        return minDist < 999998.0 ? minDist : -1.0;
      }

      // Gradient renk hesaplama - doğrudan mm değeri ile
      // Smoothstep kullanarak daha yumuşak renk geçişleri
      vec3 getGradientColor(float thickness) {
        // İlk renk (en ince)
        vec3 color = vec3(colorValues[0], colorValues[1], colorValues[2]);

        // Eğer kalınlık ilk değerden küçükse, ilk rengi kullan
        if (thickness <= colorThicknesses[0]) {
          return color;
        }

        // Eğer kalınlık son değerden büyükse, son rengi kullan
        if (colorCount > 0 && thickness >= colorThicknesses[colorCount - 1]) {
          int lastIdx = colorCount - 1;
          return vec3(colorValues[lastIdx * 3], colorValues[lastIdx * 3 + 1], colorValues[lastIdx * 3 + 2]);
        }

        // Aralıklar arasında interpolasyon - smoothstep ile
        for (int i = 0; i < 7; i++) {
          if (i >= colorCount - 1) break;

          float t1 = colorThicknesses[i];      // mm cinsinden alt sınır
          float t2 = colorThicknesses[i + 1];  // mm cinsinden üst sınır

          if (thickness >= t1 && thickness <= t2) {
            float t = (thickness - t1) / (t2 - t1);
            // Smoothstep ile daha yumuşak geçiş (CT tarama benzeri)
            t = t * t * (3.0 - 2.0 * t);
            vec3 c1 = vec3(colorValues[i * 3], colorValues[i * 3 + 1], colorValues[i * 3 + 2]);
            vec3 c2 = vec3(colorValues[(i + 1) * 3], colorValues[(i + 1) * 3 + 1], colorValues[(i + 1) * 3 + 2]);
            color = mix(c1, c2, t);
            break;
          }
        }

        return color;
      }

      void main() {
        vec3 worldNormal = normalize(vWorldNormal);
        vec3 viewNormal = normalize(vNormal);
        vec3 V = normalize(-vViewPosition);

        // Normal yönünün tersine doğru kalınlık hesapla (içe doğru)
        vec3 inwardDir = -worldNormal;

        // Kalınlık hesapla
        float thickness = calculateThicknessInDirection(vWorldPosition, inwardDir);

        // Eğer hesaplanamadıysa, tüm yönlerde minimum mesafeyi bul
        if (thickness < 0.0) {
          float minThick = 999999.0;

          // 6 ana yön kontrolü
          vec3 directions[6];
          directions[0] = vec3(1.0, 0.0, 0.0);
          directions[1] = vec3(-1.0, 0.0, 0.0);
          directions[2] = vec3(0.0, 1.0, 0.0);
          directions[3] = vec3(0.0, -1.0, 0.0);
          directions[4] = vec3(0.0, 0.0, 1.0);
          directions[5] = vec3(0.0, 0.0, -1.0);

          for (int i = 0; i < 6; i++) {
            // Normal ile aynı yönde olanları atla
            if (dot(directions[i], worldNormal) > 0.5) continue;

            float d = calculateThicknessInDirection(vWorldPosition, directions[i]);
            if (d > 0.0) minThick = min(minThick, d);
          }

          thickness = minThick < 999998.0 ? minThick : maxThickness;
        }

        // Renk hesapla - doğrudan mm değeri ile
        vec3 baseColor = getGradientColor(thickness);

        // Kritik bölge vurgulama
        bool isCritical = thickness < criticalThickness;
        if (highlightCritical && isCritical) {
          baseColor = mix(baseColor, criticalColor, criticalBrightness);
        }

        // ----------- LIGHTING --------------
        vec3 keyLight = normalize(keyLightPos);
        vec3 fillLight = normalize(fillLightPos);
        vec3 rimLight = normalize(rimLightPos);

        float keyDiffuse = max(dot(viewNormal, keyLight), 0.0) * keyLightIntensity;
        float fillDiffuse = max(dot(viewNormal, fillLight), 0.0) * fillLightIntensity;
        float rimDiffuse = max(dot(viewNormal, rimLight), 0.0) * rimLightIntensity;

        // ----------- AO / CURVATURE --------------
        vec3 dx = dFdx(vNormal);
        vec3 dy = dFdy(vNormal);
        float curvature = length(dx) + length(dy);

        float ao = 1.0 - clamp(curvature * aoIntensity, 0.0, aoMax);

        float totalLight = ambientLight + keyDiffuse + fillDiffuse + rimDiffuse;
        totalLight = clamp(totalLight * ao, ambientLight, 1.3);

        // Edge darken
        float edge = clamp(curvature * edgeIntensity, 0.0, 1.0);

        vec3 finalColor = baseColor * totalLight;
        finalColor = mix(finalColor, finalColor * (1.0 - edgeDarkness), edge);

        // Alpha - ince alanları transparan yap
        float alpha = 1.0;
        if (transparentThin && isCritical) {
          alpha = 0.7;
        }

        gl_FragColor = vec4(finalColor, alpha);
      }
    `;
  }

  /**
   * 6 yön için depth map'leri hesapla
   */
  private depthMapleriHesapla() {
    const seciliMeshler = this.seciliMeshleriAl();
    if (seciliMeshler.length === 0 || !this.anaGosterim) {
      return;
    }

    const renderer = this.rendererAl();
    if (!renderer) {
      return;
    }

    // Bounding box hesapla - use pre-allocated buffers
    this._tmpBox3.makeEmpty();
    seciliMeshler.forEach(mesh => {
      mesh.updateMatrixWorld(true);
      const geometry = mesh.geometry as THREE.BufferGeometry;
      if (!geometry.boundingBox) {
        geometry.computeBoundingBox();
      }
      // Use copy instead of clone with pre-allocated box
      const meshBox = geometry.boundingBox!.clone();
      meshBox.applyMatrix4(mesh.matrixWorld);
      this._tmpBox3.union(meshBox);
    });

    // Use pre-allocated vectors
    this._tmpBox3.getCenter(this._tmpVec3A);
    this._tmpBox3.getSize(this._tmpVec3B);
    const maxDim = Math.max(this._tmpVec3B.x, this._tmpVec3B.y, this._tmpVec3B.z) * 1.5;

    // Shader'a bounding box bilgisi gönder
    if (this.shaderMaterial) {
      this.shaderMaterial.uniforms.boundingBoxMin.value.copy(this._tmpBox3.min);
      this.shaderMaterial.uniforms.boundingBoxMax.value.copy(this._tmpBox3.max);
    }

    // Depth scene oluştur
    this.depthScene = new THREE.Scene();
    const depthMaterial = new THREE.MeshDepthMaterial({
      depthPacking: THREE.RGBADepthPacking,
      side: THREE.DoubleSide
    });

    seciliMeshler.forEach(mesh => {
      if (mesh.type === 'Mesh' && mesh.geometry) {
        mesh.updateMatrixWorld(true);
        const geometry = (mesh.geometry as THREE.BufferGeometry).clone();
        geometry.applyMatrix4(mesh.matrixWorld);
        const clonedMesh = new THREE.Mesh(geometry, depthMaterial);
        this.depthScene!.add(clonedMesh);
      }
    });

    // 6 yön için kamera ve render target oluştur
    const directions = [
      { name: 'PosX', dir: new THREE.Vector3(1, 0, 0), up: new THREE.Vector3(0, 1, 0) },
      { name: 'NegX', dir: new THREE.Vector3(-1, 0, 0), up: new THREE.Vector3(0, 1, 0) },
      { name: 'PosY', dir: new THREE.Vector3(0, 1, 0), up: new THREE.Vector3(0, 0, 1) },
      { name: 'NegY', dir: new THREE.Vector3(0, -1, 0), up: new THREE.Vector3(0, 0, -1) },
      { name: 'PosZ', dir: new THREE.Vector3(0, 0, 1), up: new THREE.Vector3(0, 1, 0) },
      { name: 'NegZ', dir: new THREE.Vector3(0, 0, -1), up: new THREE.Vector3(0, 1, 0) }
    ];

    const currentRenderTarget = renderer.getRenderTarget();

    directions.forEach(({ name, dir, up }) => {
      // Orthographic kamera
      const camera = new THREE.OrthographicCamera(
        -maxDim / 2, maxDim / 2,
        maxDim / 2, -maxDim / 2,
        0.1, maxDim * 2
      );

      // Use pre-allocated temp vector instead of clone
      this._tmpVec3C.copy(dir).multiplyScalar(maxDim);
      camera.position.copy(this._tmpVec3A).add(this._tmpVec3C);
      camera.up.copy(up);
      camera.lookAt(this._tmpVec3A);
      camera.updateMatrixWorld();
      camera.updateProjectionMatrix();

      this.depthCameras.set(name, camera);

      // Render target
      let target = this.depthTargets.get(name);
      if (!target) {
        target = new THREE.WebGLRenderTarget(this.DEPTH_TEXTURE_SIZE, this.DEPTH_TEXTURE_SIZE, {
          minFilter: THREE.NearestFilter,
          magFilter: THREE.NearestFilter,
          format: THREE.RGBAFormat,
          type: THREE.UnsignedByteType
        });
        this.depthTargets.set(name, target);
      }

      // Render
      renderer.setRenderTarget(target);
      renderer.clear();
      renderer.render(this.depthScene!, camera);

      // View-Projection matrisini shader'a gönder - use pre-allocated matrix
      if (this.shaderMaterial) {
        this._tmpMat4.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
        this.shaderMaterial.uniforms[`depthVPMatrix${name}`].value.copy(this._tmpMat4);
        this.shaderMaterial.uniforms[`depthMap${name}`].value = target.texture;
      }
    });

    renderer.setRenderTarget(currentRenderTarget);

    // Near/Far değerlerini güncelle
    if (this.shaderMaterial) {
      this.shaderMaterial.uniforms.cameraNearFar.value.set(0.1, maxDim * 2);
    }
  }

  private rendererAl(): any {
    if (!this.anaGosterim) return null;

    const possiblePaths = [
      this.anaGosterim.webGLRenderer,
      this.anaGosterim.threeRenderer,
      this.anaGosterim.renderer3d,
      this.anaGosterim._renderer,
      this.anaGosterim.renderer
    ];

    for (const possibleRenderer of possiblePaths) {
      if (possibleRenderer && typeof possibleRenderer.getRenderTarget === 'function') {
        return possibleRenderer;
      }
    }

    for (const key in this.anaGosterim) {
      const value = (this.anaGosterim as any)[key];
      if (value && typeof value.getRenderTarget === 'function') {
        return value;
      }
    }

    return null;
  }

  private depthTargetsTemizle() {
    this.depthTargets.forEach(target => {
      target.dispose();
    });
    this.depthTargets.clear();
    this.depthCameras.clear();

    if (this.depthScene) {
      this.depthScene.clear();
      this.depthScene = null;
    }
  }

  public analiziBaslat() {
    const seciliMeshler = this.seciliMeshleriAl();
    if (seciliMeshler.length === 0) {
      return;
    }

    if (this.analizModu === 'GPU' && !this.shaderMaterial) {
      return;
    }

    this.analiziDurdur();

    // Mesh'lere shader/material uygula
    seciliMeshler.forEach((mesh: THREE.Mesh) => {
      if (mesh instanceof THREE.Mesh && mesh.material) {
        this.orijinalMaterials.set(mesh, mesh.material as THREE.Material);

        const geometry = mesh.geometry as THREE.BufferGeometry;
        if (!geometry.hasAttribute('color')) {
          const positionAttr = geometry.getAttribute('position');
          if (positionAttr) {
            const vertexCount = positionAttr.count;
            const colors = new Float32Array(vertexCount * 3);
            geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
          }
        }

        if (mesh.parent) {
          mesh.parent.userData.analizMeshParent = true;
        }

        this.aktifMeshler.push(mesh);
      }
    });

    this.analizAktif = true;

    if (this.analizModu === 'PRO_GPU') {
      // PRO GPU modu: WebGPU compute shader + profesyonel post-processing
      this.proGPUAnaliziBaslat();
    } else if (this.analizModu === 'GPU') {
      // GPU modu: Shader ile depth map analizi
      this.aktifMeshler.forEach(mesh => {
        mesh.material = this.shaderMaterial!;
      });

      this.shaderParametreleriniGuncelle();

      setTimeout(() => {
        this.depthMapleriHesapla();

        // GPU için hızlı min/max tespiti yap ve otomatik skala oluştur
        this.gpuMinMaxTespitiYap();

        this.anaGosterim?.ciz();
      }, 100);
    } else {
      // CPU modu: BVH ray casting ile duvardan-duvara kalınlık
      this.cpuAnaliziBaslat();
    }

    this.CD.detectChanges();
  }

  /**
   * CPU tabanlı BVH ray casting analizi
   * Her vertex için çoklu yönde ray atarak minimum kalınlığı bulur
   * CT tarama benzeri sonuç - tüm yüzeyler renklendirilir
   */
  private async cpuAnaliziBaslat() {
    this.cpuHesaplaniyor = true;
    this.cpuIlerleme = 0;
    this.CD.detectChanges();

    // Tüm mesh'leri birleştir (BVH için)
    const mergedGeometry = this.meshleriBirlestir();
    if (!mergedGeometry) {
      this.cpuHesaplaniyor = false;
      return;
    }

    // BVH oluştur
    const bvh = new MeshBVH(mergedGeometry, { strategy: SAH });

    // DoubleSide material ile mesh oluştur
    const tempMaterial = new THREE.MeshBasicMaterial({ side: THREE.DoubleSide });
    const tempMesh = new THREE.Mesh(mergedGeometry, tempMaterial);
    (tempMesh.geometry as any).boundsTree = bvh;

    // Raycaster hazırla
    const raycaster = new THREE.Raycaster();
    raycaster.firstHitOnly = false;

    // Pre-allocate ray direction buffers (reused for each vertex)
    const rayYonleri: THREE.Vector3[] = [
      new THREE.Vector3(),
      new THREE.Vector3(),
      new THREE.Vector3(),
      new THREE.Vector3(),
      new THREE.Vector3()
    ];
    const tangent = new THREE.Vector3();
    const bitangent = new THREE.Vector3();
    const tmpAxisX = new THREE.Vector3(1, 0, 0);
    const tmpAxisY = new THREE.Vector3(0, 1, 0);

    // Çoklu ray yönleri - normal + etrafındaki koniler (zero-allocation version)
    const rayYonleriOlustur = (normal: THREE.Vector3): void => {
      // Ana yön: Normal'in tersi
      rayYonleri[0].copy(normal).negate();

      // Tangent ve bitangent hesapla - use pre-allocated vectors
      if (Math.abs(normal.x) < 0.9) {
        tangent.crossVectors(normal, tmpAxisX).normalize();
      } else {
        tangent.crossVectors(normal, tmpAxisY).normalize();
      }
      bitangent.crossVectors(normal, tangent).normalize();

      // Koni içinde ek yönler (30 derece açıyla)
      const coneAngle = Math.PI / 6; // 30 derece
      const cosAngle = Math.cos(coneAngle);
      const sinAngle = Math.sin(coneAngle);

      // 4 yön (koni kenarları) - reuse pre-allocated vectors
      for (let i = 0; i < 4; i++) {
        const phi = (i / 4) * Math.PI * 2;
        rayYonleri[i + 1].copy(normal).negate().multiplyScalar(cosAngle);
        rayYonleri[i + 1].addScaledVector(tangent, sinAngle * Math.cos(phi));
        rayYonleri[i + 1].addScaledVector(bitangent, sinAngle * Math.sin(phi));
        rayYonleri[i + 1].normalize();
      }
    };

    // Her mesh için kalınlık hesapla
    let toplamVertex = 0;
    let islenenVertex = 0;

    this.aktifMeshler.forEach(mesh => {
      const posAttr = (mesh.geometry as THREE.BufferGeometry).getAttribute('position');
      if (posAttr) toplamVertex += posAttr.count;
    });

    // Sonuç istatistikleri
    let minKalinlik = Infinity;
    let maxKalinlik = 0;
    let toplamKalinlik = 0;
    let kritikSayisi = 0;
    let gecerliOlcumSayisi = 0;

    const EPSILON = 0.001;
    const MIN_THICKNESS = 0.01;  // Minimum kabul edilebilir kalınlık

    for (const mesh of this.aktifMeshler) {
      const geometry = mesh.geometry as THREE.BufferGeometry;
      const positionAttr = geometry.getAttribute('position');
      const normalAttr = geometry.getAttribute('normal');

      if (!positionAttr || !normalAttr) continue;

      mesh.updateMatrixWorld(true);

      const vertexCount = positionAttr.count;
      const kalinliklar = new Float32Array(vertexCount);

      // Renk buffer'ı
      let colorAttr = geometry.getAttribute('color') as THREE.BufferAttribute;
      if (!colorAttr) {
        const colors = new Float32Array(vertexCount * 3);
        colorAttr = new THREE.BufferAttribute(colors, 3);
        geometry.setAttribute('color', colorAttr);
      }

      const tempPos = new THREE.Vector3();
      const tempNormal = new THREE.Vector3();
      const worldPos = new THREE.Vector3();
      const worldNormal = new THREE.Vector3();
      const rayOrigin = new THREE.Vector3();
      const normalMatrix = new THREE.Matrix3().getNormalMatrix(mesh.matrixWorld);

      const batchSize = 200;

      for (let i = 0; i < vertexCount; i++) {
        tempPos.fromBufferAttribute(positionAttr, i);
        worldPos.copy(tempPos).applyMatrix4(mesh.matrixWorld);

        tempNormal.fromBufferAttribute(normalAttr, i);
        worldNormal.copy(tempNormal).applyMatrix3(normalMatrix).normalize();

        // Çoklu yönde ray at, minimum kalınlığı bul - zero-allocation
        rayYonleriOlustur(worldNormal);
        let minDist = Infinity;

        // Use pre-allocated rayYonleri array (no loop allocation)
        for (let rayIdx = 0; rayIdx < rayYonleri.length; rayIdx++) {
          const rayDir = rayYonleri[rayIdx];

          // Ray başlangıcını yüzeyden hafifçe kaydır
          rayOrigin.copy(worldPos).addScaledVector(rayDir, EPSILON);

          raycaster.set(rayOrigin, rayDir);
          const intersects = raycaster.intersectObject(tempMesh, false);

          for (const hit of intersects) {
            const dist = hit.distance + EPSILON;

            // Self-intersection filtresi
            if (dist < MIN_THICKNESS) continue;

            // Minimum mesafeyi güncelle
            if (dist < minDist) {
              minDist = dist;
            }
            break; // Bu yön için ilk geçerli kesişimi aldık
          }
        }

        const kalinlik = minDist < Infinity ? minDist : -1;
        kalinliklar[i] = kalinlik;

        // Renk hesapla - use pre-allocated color buffer
        if (kalinlik > 0) {
          this.kalinliktanRenkHesaplaInPlace(kalinlik, this._tmpColor);
          colorAttr.setXYZ(i, this._tmpColor.r, this._tmpColor.g, this._tmpColor.b);

          minKalinlik = Math.min(minKalinlik, kalinlik);
          maxKalinlik = Math.max(maxKalinlik, kalinlik);
          toplamKalinlik += kalinlik;
          gecerliOlcumSayisi++;

          if (kalinlik < this.kritikKalinlik) {
            kritikSayisi++;
          }
        } else {
          // Ölçüm yapılamadı - en kalın rengi kullan (açık yüzey)
          this.kalinliktanRenkHesaplaInPlace(this.renkSkalasi[this.renkSkalasi.length - 1].kalinlik, this._tmpColor);
          colorAttr.setXYZ(i, this._tmpColor.r, this._tmpColor.g, this._tmpColor.b);
        }

        islenenVertex++;

        if (i % batchSize === 0) {
          this.cpuIlerleme = Math.round((islenenVertex / toplamVertex) * 100);
          this.CD.detectChanges();
          await new Promise(resolve => setTimeout(resolve, 0));
        }
      }

      colorAttr.needsUpdate = true;
      geometry.attributes.color.needsUpdate = true;
      this.cpuKalinlikVerileri.set(mesh, kalinliklar);

      // Kalınlık değerlerini attribute olarak ekle (shader için)
      const thicknessAttr = new THREE.BufferAttribute(kalinliklar, 1);
      geometry.setAttribute('thickness', thicknessAttr);

      // Smooth gradient shader material oluştur
      const smoothMaterial = this.createSmoothThicknessMaterial();
      mesh.material = smoothMaterial;

      this.anaGosterim?.ciz();
    }

    mergedGeometry.dispose();

    if (gecerliOlcumSayisi > 0) {
      this.sonuclar = {
        minBulunan: minKalinlik,
        maxBulunan: maxKalinlik,
        ortalamaKalinlik: toplamKalinlik / gecerliOlcumSayisi,
        kritikYuzde: (kritikSayisi / gecerliOlcumSayisi) * 100
      };

      // Otomatik renk skalası oluştur (min-max aralığına göre)
      this.otomatikRenkSkalasıOlustur(minKalinlik, maxKalinlik);

      // Material'leri yeni skala ile güncelle
      for (const mesh of this.aktifMeshler) {
        const smoothMaterial = this.createSmoothThicknessMaterial();
        mesh.material = smoothMaterial;
      }
    }

    this.cpuHesaplaniyor = false;
    this.cpuIlerleme = 100;
    this.anaGosterim?.ciz();
    this.CD.detectChanges();
  }

  /**
   * Min-Max kalınlık değerlerine göre otomatik renk skalası oluştur
   * 8 noktalı gradient: Kırmızı (min) -> Mavi (max)
   */
  private otomatikRenkSkalasıOlustur(minKalinlik: number, maxKalinlik: number) {
    // Biraz padding ekle (daha iyi görselleştirme için)
    const padding = (maxKalinlik - minKalinlik) * 0.05;
    const min = Math.max(0.1, minKalinlik - padding);
    const max = maxKalinlik + padding;
    const aralik = max - min;

    // 8 noktalı gradient renk skalası
    this.renkSkalasi = [
      { kalinlik: parseFloat(min.toFixed(2)), renk: '#ff0000' },                          // Kırmızı
      { kalinlik: parseFloat((min + aralik * 0.14).toFixed(2)), renk: '#ff6600' },        // Turuncu
      { kalinlik: parseFloat((min + aralik * 0.28).toFixed(2)), renk: '#ffff00' },        // Sarı
      { kalinlik: parseFloat((min + aralik * 0.42).toFixed(2)), renk: '#88ff00' },        // Açık yeşil
      { kalinlik: parseFloat((min + aralik * 0.57).toFixed(2)), renk: '#00ff00' },        // Yeşil
      { kalinlik: parseFloat((min + aralik * 0.71).toFixed(2)), renk: '#00ffaa' },        // Turkuaz
      { kalinlik: parseFloat((min + aralik * 0.85).toFixed(2)), renk: '#00aaff' },        // Açık mavi
      { kalinlik: parseFloat(max.toFixed(2)), renk: '#0066ff' }                           // Mavi
    ];

    // Kritik kalınlığı da güncelle (min değerine yakın)
    this.kritikKalinlik = parseFloat((min + aralik * 0.1).toFixed(2));

    this.CD.detectChanges();
  }

  /**
   * Kullanıcı tarafından çağrılabilen otomatik skala hesaplama
   * Sonuçlar mevcutsa min-max değerlerine göre skala oluşturur
   */
  public otomatikSkalaHesapla() {
    if (this.sonuclar) {
      this.otomatikRenkSkalasıOlustur(this.sonuclar.minBulunan, this.sonuclar.maxBulunan);

      // Aktif mesh'lerin material'lerini güncelle
      if (this.analizModu === 'CPU') {
        for (const mesh of this.aktifMeshler) {
          const smoothMaterial = this.createSmoothThicknessMaterial();
          mesh.material = smoothMaterial;
        }
      } else {
        this.shaderParametreleriniGuncelle();
      }

      this.anaGosterim?.ciz();
    }
  }

  /**
   * GPU modu için hızlı min/max kalınlık tespiti
   * Mesh vertex'lerinden sampling yaparak yaklaşık değerleri bulur
   */
  private gpuMinMaxTespitiYap() {
    if (!this.shaderMaterial || this.aktifMeshler.length === 0) return;

    // Use pre-allocated bounding box
    this._tmpBox3.makeEmpty();
    this.aktifMeshler.forEach(mesh => {
      mesh.updateMatrixWorld(true);
      const geometry = mesh.geometry as THREE.BufferGeometry;
      if (!geometry.boundingBox) {
        geometry.computeBoundingBox();
      }
      const meshBox = geometry.boundingBox!.clone();
      meshBox.applyMatrix4(mesh.matrixWorld);
      this._tmpBox3.union(meshBox);
    });

    // Use pre-allocated size vector
    this._tmpBox3.getSize(this._tmpVec3A);

    // Bounding box'tan yaklaşık kalınlık hesapla
    // En küçük boyut genellikle duvar kalınlığına yakın
    const minDim = Math.min(this._tmpVec3A.x, this._tmpVec3A.y, this._tmpVec3A.z);
    const maxDim = Math.max(this._tmpVec3A.x, this._tmpVec3A.y, this._tmpVec3A.z);

    // Vertex'lerden sampling yaparak daha doğru min/max bul
    let minKalinlik = Infinity;
    let maxKalinlik = 0;
    let toplamKalinlik = 0;
    let ornekSayisi = 0;

    // Her mesh'ten örnek al - use pre-allocated vectors
    for (const mesh of this.aktifMeshler) {
      const geometry = mesh.geometry as THREE.BufferGeometry;
      const positionAttr = geometry.getAttribute('position');
      const normalAttr = geometry.getAttribute('normal');

      if (!positionAttr || !normalAttr) continue;

      mesh.updateMatrixWorld(true);
      this._tmpMat3.getNormalMatrix(mesh.matrixWorld);

      // Her 50 vertex'ten birini örnekle (hız için)
      const sampleRate = Math.max(1, Math.floor(positionAttr.count / 100));

      for (let i = 0; i < positionAttr.count; i += sampleRate) {
        // Use pre-allocated vectors - zero allocation in loop
        this._tmpVec3B.fromBufferAttribute(positionAttr, i);
        this._tmpVec3C.copy(this._tmpVec3B).applyMatrix4(mesh.matrixWorld);

        this._tmpVec3D.fromBufferAttribute(normalAttr, i);
        this._tmpVec3E.copy(this._tmpVec3D).applyMatrix3(this._tmpMat3).normalize();

        // Depth map'lerden kalınlık tahmin et
        const kalinlik = this.depthMapKalinlikTahminOptimized(this._tmpVec3C, this._tmpVec3E);

        if (kalinlik > 0 && kalinlik < Infinity) {
          minKalinlik = Math.min(minKalinlik, kalinlik);
          maxKalinlik = Math.max(maxKalinlik, kalinlik);
          toplamKalinlik += kalinlik;
          ornekSayisi++;
        }
      }
    }

    // Eğer geçerli sonuç bulunamadıysa bounding box'tan tahmin et
    if (ornekSayisi === 0 || minKalinlik === Infinity) {
      minKalinlik = minDim * 0.1;
      maxKalinlik = minDim * 0.8;
    }

    // Sonuçları kaydet
    this.sonuclar = {
      minBulunan: minKalinlik,
      maxBulunan: maxKalinlik,
      ortalamaKalinlik: ornekSayisi > 0 ? toplamKalinlik / ornekSayisi : (minKalinlik + maxKalinlik) / 2,
      kritikYuzde: 0 // GPU modunda kritik yüzde hesaplanmıyor
    };

    // Otomatik renk skalası oluştur
    this.otomatikRenkSkalasıOlustur(minKalinlik, maxKalinlik);

    // Shader'ı güncelle
    this.shaderParametreleriniGuncelle();

    this.CD.detectChanges();
  }

  /**
   * Depth map'lerden kalınlık tahmin et
   * World position ve normal kullanarak karşı yüzeye mesafeyi hesaplar
   */
  private depthMapKalinlikTahmin(worldPos: THREE.Vector3, worldNormal: THREE.Vector3, boundingBox: THREE.Box3): number {
    const size = new THREE.Vector3();
    boundingBox.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z);

    // Normal yönünün tersinde (içe doğru) mesafe hesapla
    const inwardDir = worldNormal.clone().negate();

    // 6 yönden en uygun olanı seç
    let minDist = Infinity;

    const directions = [
      { dir: new THREE.Vector3(1, 0, 0), camera: this.depthCameras.get('NegX'), target: this.depthTargets.get('NegX') },
      { dir: new THREE.Vector3(-1, 0, 0), camera: this.depthCameras.get('PosX'), target: this.depthTargets.get('PosX') },
      { dir: new THREE.Vector3(0, 1, 0), camera: this.depthCameras.get('NegY'), target: this.depthTargets.get('NegY') },
      { dir: new THREE.Vector3(0, -1, 0), camera: this.depthCameras.get('PosY'), target: this.depthTargets.get('PosY') },
      { dir: new THREE.Vector3(0, 0, 1), camera: this.depthCameras.get('NegZ'), target: this.depthTargets.get('NegZ') },
      { dir: new THREE.Vector3(0, 0, -1), camera: this.depthCameras.get('PosZ'), target: this.depthTargets.get('PosZ') }
    ];

    for (const { dir, camera, target } of directions) {
      if (!camera || !target) continue;

      // İçe doğru yönle uyumlu mu kontrol et
      const dot = inwardDir.dot(dir);
      if (dot < 0.3) continue; // Bu yön uygun değil

      // Basit mesafe tahmini: bounding box boyutuna göre
      const dist = maxDim * (1 - Math.abs(dot)) * 0.5;
      if (dist > 0 && dist < minDist) {
        minDist = dist;
      }
    }

    // Bounding box'a göre makul bir değer döndür
    if (minDist === Infinity) {
      return Math.min(size.x, size.y, size.z) * 0.3;
    }

    return minDist;
  }

  /**
   * Optimized version of depthMapKalinlikTahmin - uses pre-allocated tmpBox3 and tmpVec3A
   * Called from gpuMinMaxTespitiYap where boundingBox is already in tmpBox3 and size in tmpVec3A
   */
  private depthMapKalinlikTahminOptimized(worldPos: THREE.Vector3, worldNormal: THREE.Vector3): number {
    const maxDim = Math.max(this._tmpVec3A.x, this._tmpVec3A.y, this._tmpVec3A.z);

    // Normal yönünün tersinde (içe doğru) mesafe hesapla - use pre-allocated vector
    this._tmpVec3F.copy(worldNormal).negate();

    // 6 yönden en uygun olanı seç
    let minDist = Infinity;

    const directions = [
      { dir: new THREE.Vector3(1, 0, 0), camera: this.depthCameras.get('NegX'), target: this.depthTargets.get('NegX') },
      { dir: new THREE.Vector3(-1, 0, 0), camera: this.depthCameras.get('PosX'), target: this.depthTargets.get('PosX') },
      { dir: new THREE.Vector3(0, 1, 0), camera: this.depthCameras.get('NegY'), target: this.depthTargets.get('NegY') },
      { dir: new THREE.Vector3(0, -1, 0), camera: this.depthCameras.get('PosY'), target: this.depthTargets.get('PosY') },
      { dir: new THREE.Vector3(0, 0, 1), camera: this.depthCameras.get('NegZ'), target: this.depthTargets.get('NegZ') },
      { dir: new THREE.Vector3(0, 0, -1), camera: this.depthCameras.get('PosZ'), target: this.depthTargets.get('PosZ') }
    ];

    for (const { dir, camera, target } of directions) {
      if (!camera || !target) continue;

      // İçe doğru yönle uyumlu mu kontrol et
      const dot = this._tmpVec3F.dot(dir);
      if (dot < 0.3) continue; // Bu yön uygun değil

      // Basit mesafe tahmini: bounding box boyutuna göre
      const dist = maxDim * (1 - Math.abs(dot)) * 0.5;
      if (dist > 0 && dist < minDist) {
        minDist = dist;
      }
    }

    // Bounding box'a göre makul bir değer döndür
    if (minDist === Infinity) {
      return Math.min(this._tmpVec3A.x, this._tmpVec3A.y, this._tmpVec3A.z) * 0.3;
    }

    return minDist;
  }

  /**
   * Tüm mesh'leri tek bir geometry'de birleştir (BVH için)
   * THREE.js BufferGeometryUtils kullanarak güvenilir birleştirme
   */
  private meshleriBirlestir(): THREE.BufferGeometry | null {
    const geometries: THREE.BufferGeometry[] = [];

    this.aktifMeshler.forEach(mesh => {
      if (mesh.geometry) {
        mesh.updateMatrixWorld(true);
        const srcGeom = mesh.geometry as THREE.BufferGeometry;

        // Geometry'yi klonla ve world transform uygula
        const clonedGeom = srcGeom.clone();
        clonedGeom.applyMatrix4(mesh.matrixWorld);

        // Non-indexed geometry'yi indexed'a çevir (BVH için gerekli)
        if (!clonedGeom.getIndex()) {
          const posAttr = clonedGeom.getAttribute('position');
          if (posAttr) {
            const indices: number[] = [];
            for (let i = 0; i < posAttr.count; i++) {
              indices.push(i);
            }
            clonedGeom.setIndex(indices);
          }
        }

        // Normaller yoksa hesapla
        if (!clonedGeom.getAttribute('normal')) {
          clonedGeom.computeVertexNormals();
        }

        geometries.push(clonedGeom);
      }
    });

    if (geometries.length === 0) return null;

    if (geometries.length === 1) {
      return geometries[0];
    }

    // THREE.js BufferGeometryUtils ile birleştir
    try {
      const merged = mergeGeometries(geometries, false);
      // Orijinal geometrileri temizle
      geometries.forEach(geom => geom.dispose());
      return merged;
    } catch (e) {
      // Fallback: İlk geometry'yi döndür
      return geometries[0];
    }
  }

  /**
   * CPU modu için smooth gradient shader material oluştur
   * Fragment shader'da kalınlık interpolasyonu yapılır - üçgensel görünüm olmaz
   */
  private createSmoothThicknessMaterial(): THREE.ShaderMaterial {
    const sortedSkala = [...this.renkSkalasi].sort((a, b) => a.kalinlik - b.kalinlik);
    const maxColors = 8;

    const colorThicknesses = new Float32Array(maxColors);
    const colorValues = new Float32Array(maxColors * 3);

    for (let i = 0; i < maxColors; i++) {
      if (i < sortedSkala.length) {
        colorThicknesses[i] = sortedSkala[i].kalinlik;
        const color = new THREE.Color(sortedSkala[i].renk);
        colorValues[i * 3] = color.r;
        colorValues[i * 3 + 1] = color.g;
        colorValues[i * 3 + 2] = color.b;
      } else {
        colorThicknesses[i] = 9999.0;
        colorValues[i * 3] = 0.0;
        colorValues[i * 3 + 1] = 0.5;
        colorValues[i * 3 + 2] = 1.0;
      }
    }

    return new THREE.ShaderMaterial({
      uniforms: {
        colorThicknesses: { value: colorThicknesses },
        colorValues: { value: colorValues },
        colorCount: { value: sortedSkala.length },
        criticalThickness: { value: this.kritikKalinlik },
        highlightCritical: { value: this.highlightCritical },
        criticalColor: { value: new THREE.Vector3(1.0, 0.0, 0.0) },
        criticalBrightness: { value: this.kritikParlaklik },
        // Lighting
        ambientLight: { value: 0.4 },
        lightDirection: { value: new THREE.Vector3(0.5, 1.0, 0.5).normalize() }
      },
      vertexShader: `
        attribute float thickness;
        varying float vThickness;
        varying vec3 vNormal;
        varying vec3 vViewPosition;

        void main() {
          vThickness = thickness;
          vNormal = normalize(normalMatrix * normal);

          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          vViewPosition = -mvPosition.xyz;

          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform float colorThicknesses[8];
        uniform float colorValues[24];
        uniform int colorCount;
        uniform float criticalThickness;
        uniform bool highlightCritical;
        uniform vec3 criticalColor;
        uniform float criticalBrightness;
        uniform float ambientLight;
        uniform vec3 lightDirection;

        varying float vThickness;
        varying vec3 vNormal;
        varying vec3 vViewPosition;

        vec3 getGradientColor(float thickness) {
          // İlk renk
          vec3 color = vec3(colorValues[0], colorValues[1], colorValues[2]);

          if (thickness <= colorThicknesses[0]) {
            return color;
          }

          if (thickness >= colorThicknesses[colorCount - 1]) {
            int lastIdx = colorCount - 1;
            return vec3(colorValues[lastIdx * 3], colorValues[lastIdx * 3 + 1], colorValues[lastIdx * 3 + 2]);
          }

          // Smooth interpolasyon
          for (int i = 0; i < 7; i++) {
            if (i >= colorCount - 1) break;

            float t1 = colorThicknesses[i];
            float t2 = colorThicknesses[i + 1];

            if (thickness >= t1 && thickness <= t2) {
              float t = (thickness - t1) / (t2 - t1);
              // Smoothstep ile daha yumuşak geçiş
              t = t * t * (3.0 - 2.0 * t);

              vec3 c1 = vec3(colorValues[i * 3], colorValues[i * 3 + 1], colorValues[i * 3 + 2]);
              vec3 c2 = vec3(colorValues[(i + 1) * 3], colorValues[(i + 1) * 3 + 1], colorValues[(i + 1) * 3 + 2]);
              color = mix(c1, c2, t);
              break;
            }
          }

          return color;
        }

        void main() {
          float thickness = vThickness;

          // Geçersiz kalınlık için maksimum renk
          if (thickness < 0.0) {
            thickness = colorThicknesses[colorCount - 1];
          }

          vec3 baseColor = getGradientColor(thickness);

          // Kritik bölge vurgulama
          if (highlightCritical && thickness < criticalThickness && thickness > 0.0) {
            baseColor = mix(baseColor, criticalColor, criticalBrightness);
          }

          // Basit diffuse lighting
          vec3 normal = normalize(vNormal);
          vec3 lightDir = normalize(lightDirection);
          float diff = max(dot(normal, lightDir), 0.0);

          // Ambient + diffuse
          float lighting = ambientLight + (1.0 - ambientLight) * diff;

          vec3 finalColor = baseColor * lighting;

          gl_FragColor = vec4(finalColor, 1.0);
        }
      `,
      side: THREE.DoubleSide
    });
  }

  /**
   * Kalınlık değerinden renk hesapla (mm cinsinden)
   */
  private kalinliktanRenkHesapla(kalinlik: number): THREE.Color {
    const sortedSkala = [...this.renkSkalasi].sort((a, b) => a.kalinlik - b.kalinlik);

    // İlk değerden küçükse
    if (kalinlik <= sortedSkala[0].kalinlik) {
      return new THREE.Color(sortedSkala[0].renk);
    }

    // Son değerden büyükse
    if (kalinlik >= sortedSkala[sortedSkala.length - 1].kalinlik) {
      return new THREE.Color(sortedSkala[sortedSkala.length - 1].renk);
    }

    // Arada ise interpolasyon
    for (let i = 0; i < sortedSkala.length - 1; i++) {
      const t1 = sortedSkala[i].kalinlik;
      const t2 = sortedSkala[i + 1].kalinlik;

      if (kalinlik >= t1 && kalinlik <= t2) {
        const t = (kalinlik - t1) / (t2 - t1);
        const c1 = new THREE.Color(sortedSkala[i].renk);
        const c2 = new THREE.Color(sortedSkala[i + 1].renk);
        return c1.lerp(c2, t);
      }
    }

    return new THREE.Color(0x888888);
  }

  /**
   * Zero-allocation version - modifies targetColor in-place
   * Used in hot paths like CPU analysis loops
   */
  private kalinliktanRenkHesaplaInPlace(kalinlik: number, targetColor: THREE.Color): void {
    const sortedSkala = [...this.renkSkalasi].sort((a, b) => a.kalinlik - b.kalinlik);

    // İlk değerden küçükse
    if (kalinlik <= sortedSkala[0].kalinlik) {
      targetColor.set(sortedSkala[0].renk);
      return;
    }

    // Son değerden büyükse
    if (kalinlik >= sortedSkala[sortedSkala.length - 1].kalinlik) {
      targetColor.set(sortedSkala[sortedSkala.length - 1].renk);
      return;
    }

    // Arada ise interpolasyon - reuse pre-allocated colors
    for (let i = 0; i < sortedSkala.length - 1; i++) {
      const t1 = sortedSkala[i].kalinlik;
      const t2 = sortedSkala[i + 1].kalinlik;

      if (kalinlik >= t1 && kalinlik <= t2) {
        const t = (kalinlik - t1) / (t2 - t1);
        targetColor.set(sortedSkala[i].renk);
        const c2 = new THREE.Color(sortedSkala[i + 1].renk);
        targetColor.lerp(c2, t);
        return;
      }
    }

    targetColor.set(0x888888);
  }

  public analiziDurdur() {
    // Render döngüsünü durdur
    this.stopRenderLoop();

    this.aktifMeshler.forEach(mesh => {
      const orijinalMaterial = this.orijinalMaterials.get(mesh);
      if (orijinalMaterial) {
        mesh.material = orijinalMaterial;
      }

      // Orijinal geometry'yi geri yükle (smooth geometry kullanıldıysa)
      const orijinalGeometry = this.orijinalGeometries.get(mesh);
      if (orijinalGeometry) {
        // Smooth geometry'yi dispose et
        const smoothGeometry = mesh.geometry as THREE.BufferGeometry;
        if (smoothGeometry !== orijinalGeometry) {
          smoothGeometry.dispose();
        }
        // Orijinali geri yükle
        mesh.geometry = orijinalGeometry;
      } else {
        // Orijinal geometry yoksa sadece attribute'ları temizle
        const geometry = mesh.geometry as THREE.BufferGeometry;
        if (geometry.hasAttribute('thickness')) {
          geometry.deleteAttribute('thickness');
        }
        if (geometry.hasAttribute('sssFactor')) {
          geometry.deleteAttribute('sssFactor');
        }
      }

      if (mesh.parent && mesh.parent.userData?.analizMeshParent) {
        delete mesh.parent.userData.analizMeshParent;
      }
    });

    this.aktifMeshler = [];
    this.orijinalMaterials.clear();
    this.orijinalGeometries.clear();
    this.analizAktif = false;
    this.sonuclar = null;
    this.gelismisIstatistikler = null;

    // CPU modu temizliği
    this.bvhMeshler.clear();
    this.cpuKalinlikVerileri.clear();
    this.cpuHesaplaniyor = false;
    this.cpuIlerleme = 0;

    // Compositor temizliği
    if (this.thicknessCompositor) {
      this.thicknessCompositor.dispose();
      this.thicknessCompositor = null;
    }

    if (this.thicknessDataMaterial) {
      this.thicknessDataMaterial.dispose();
      this.thicknessDataMaterial = null;
    }

    if (this.directColorMaterial) {
      this.directColorMaterial.dispose();
      this.directColorMaterial = null;
    }

    this.anaGosterim?.ciz();
    this.CD.detectChanges();
  }

  private seciliMeshleriAl(): THREE.Mesh[] {
    const meshler: THREE.Mesh[] = [];

    if (!this.anaGosterim) {
      return meshler;
    }

    const seciliGruplar = [];

    if (this.anaGosterim.secililer?.grupSecimlerUzerinde) {
      seciliGruplar.push(this.anaGosterim.secililer.grupSecimlerUzerinde);
    }
    if (this.anaGosterim.secililer?.grupSecimler2Uzerinde) {
      seciliGruplar.push(this.anaGosterim.secililer.grupSecimler2Uzerinde);
    }

    // Seçili gruplardaki mesh'leri al
    seciliGruplar.forEach((grup: any) => {
      if (grup && grup.children) {
        grup.children.forEach((child: any) => {
          if (child.mesh && child.mesh instanceof THREE.Mesh) {
            meshler.push(child.mesh);
          }
        });
      }
    });

    // Eğer hiç seçim yoksa, sahnedeki mesh'leri al
    if (meshler.length === 0 && this.anaGosterim.sahneler?.[0]) {
      this.anaGosterim.sahneler[0].traverse((child: any) => {
        if (child.mesh && child.mesh instanceof THREE.Mesh && child.mesh.visible) {
          meshler.push(child.mesh);
        }
      });
    }

    return meshler;
  }

  public shaderParametreleriniGuncelle() {
    if (!this.shaderMaterial) return;

    // Renk skalasından min/max değerleri al
    const sortedSkala = [...this.renkSkalasi].sort((a, b) => a.kalinlik - b.kalinlik);
    const minKalinlik = sortedSkala.length > 0 ? sortedSkala[0].kalinlik : 0.5;
    const maxKalinlik = sortedSkala.length > 0 ? sortedSkala[sortedSkala.length - 1].kalinlik : 10.0;

    // Kalınlık parametreleri
    this.shaderMaterial.uniforms.minThickness.value = minKalinlik;
    this.shaderMaterial.uniforms.maxThickness.value = maxKalinlik;
    this.shaderMaterial.uniforms.criticalThickness.value = this.kritikKalinlik;
    this.shaderMaterial.uniforms.rayMarchSteps.value = this.rayMarchSteps;
    this.shaderMaterial.uniforms.rayMarchPrecision.value = this.rayMarchPrecision;

    // Görsel ayarlar
    this.shaderMaterial.uniforms.highlightCritical.value = this.highlightCritical;
    const kritikRenkObj = new THREE.Color(this.kritikRenk);
    this.shaderMaterial.uniforms.criticalColor.value.set(kritikRenkObj.r, kritikRenkObj.g, kritikRenkObj.b);
    this.shaderMaterial.uniforms.criticalBrightness.value = this.kritikParlaklik;
    this.shaderMaterial.uniforms.transparentThin.value = this.transparentThinAreas;

    // Lighting
    this.shaderMaterial.uniforms.keyLightIntensity.value = this.keyLightIntensity;
    this.shaderMaterial.uniforms.fillLightIntensity.value = this.fillLightIntensity;
    this.shaderMaterial.uniforms.rimLightIntensity.value = this.rimLightIntensity;
    this.shaderMaterial.uniforms.ambientLight.value = this.ambientLight;

    // AO & Edge
    this.shaderMaterial.uniforms.aoIntensity.value = this.aoIntensity;
    this.shaderMaterial.uniforms.aoMax.value = this.aoMax;
    this.shaderMaterial.uniforms.edgeIntensity.value = this.edgeIntensity;
    this.shaderMaterial.uniforms.edgeDarkness.value = this.edgeDarkness;

    // Renk skalasını güncelle
    this.renkSkalasiniGuncelle();

    this.anaGosterim?.ciz();
    this.CD.detectChanges();
  }

  private renkSkalasiniGuncelle() {
    if (!this.shaderMaterial) return;

    const maxColors = 8;
    const colorThicknesses = new Float32Array(maxColors);
    const colorValues = new Float32Array(maxColors * 3);

    // Sıralı renk skalası (kalınlığa göre)
    const sortedSkala = [...this.renkSkalasi].sort((a, b) => a.kalinlik - b.kalinlik);

    for (let i = 0; i < maxColors; i++) {
      if (i < sortedSkala.length) {
        const item = sortedSkala[i];
        colorThicknesses[i] = item.kalinlik;  // mm cinsinden
        const color = new THREE.Color(item.renk);
        colorValues[i * 3] = color.r;
        colorValues[i * 3 + 1] = color.g;
        colorValues[i * 3 + 2] = color.b;
      } else {
        colorThicknesses[i] = 9999.0;
        colorValues[i * 3] = 0.0;
        colorValues[i * 3 + 1] = 0.0;
        colorValues[i * 3 + 2] = 1.0;
      }
    }

    this.shaderMaterial.uniforms.colorThicknesses.value = colorThicknesses;
    this.shaderMaterial.uniforms.colorValues.value = colorValues;
    this.shaderMaterial.uniforms.colorCount.value = sortedSkala.length;
  }

  public renkSkalaDegisti(index: number, field: 'kalinlik' | 'renk', value: any) {
    if (field === 'kalinlik') {
      this.renkSkalasi[index].kalinlik = parseFloat(value);
      // Sıralamayı güncelle
      this.renkSkalasi.sort((a, b) => a.kalinlik - b.kalinlik);
    } else {
      this.renkSkalasi[index].renk = value;
    }
    this.shaderParametreleriniGuncelle();
  }

  public renkSkalaEkle() {
    if (this.renkSkalasi.length >= 8) return;

    // Mevcut değerlerin ortasına yeni bir değer ekle
    const sortedSkala = [...this.renkSkalasi].sort((a, b) => a.kalinlik - b.kalinlik);
    const midIndex = Math.floor(sortedSkala.length / 2);
    const newKalinlik = sortedSkala.length > 1
      ? (sortedSkala[midIndex - 1].kalinlik + sortedSkala[midIndex].kalinlik) / 2
      : 5.0;

    this.renkSkalasi.push({
      kalinlik: newKalinlik,
      renk: '#ffffff'
    });
    this.renkSkalasi.sort((a, b) => a.kalinlik - b.kalinlik);
    this.shaderParametreleriniGuncelle();
  }

  public renkSkalaSil(index: number) {
    if (this.renkSkalasi.length <= 2) return;
    this.renkSkalasi.splice(index, 1);
    this.shaderParametreleriniGuncelle();
  }

  public varsayilanDegerleriYukle() {
    this.kritikKalinlik = 1.0;
    this.rayMarchSteps = 64;
    this.rayMarchPrecision = 0.001;

    // Varsayılan renk skalası: Geniş aralık (1mm - 50mm)
    this.renkSkalasi = [
      { kalinlik: 1.0, renk: '#ff0000' },   // Çok ince - Kırmızı
      { kalinlik: 5.0, renk: '#ff6600' },   // İnce - Turuncu
      { kalinlik: 10.0, renk: '#ffff00' },  // Orta-ince - Sarı
      { kalinlik: 15.0, renk: '#88ff00' },  // Orta - Açık yeşil
      { kalinlik: 20.0, renk: '#00ff00' },  // Normal - Yeşil
      { kalinlik: 25.0, renk: '#00ffaa' },  // Kalın - Turkuaz
      { kalinlik: 35.0, renk: '#00aaff' },  // Çok kalın - Açık mavi
      { kalinlik: 50.0, renk: '#0066ff' }   // Maksimum - Mavi
    ];

    this.highlightCritical = true;
    this.kritikRenk = '#ff0000';
    this.kritikParlaklik = 0.5;
    this.transparentThinAreas = false;

    this.keyLightIntensity = 0.6;
    this.fillLightIntensity = 0.4;
    this.rimLightIntensity = 0.3;
    this.ambientLight = 0.3;
    this.aoIntensity = 3.0;
    this.aoMax = 0.5;
    this.edgeIntensity = 2.0;
    this.edgeDarkness = 0.3;

    this.shaderParametreleriniGuncelle();
  }

  // Utility: RGB to Hex
  public rgbToHex(r: number, g: number, b: number): string {
    const toHex = (c: number) => {
      const hex = Math.round(c * 255).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    return '#' + toHex(r) + toHex(g) + toHex(b);
  }

  ngOnDestroy(): void {
    this.analiziDurdur();
    this.depthTargetsTemizle();
    this.stopRenderLoop();

    if (this.shaderMaterial) {
      this.shaderMaterial.dispose();
      this.shaderMaterial = null;
    }

    if (this.gpuAnalyzer) {
      this.gpuAnalyzer.dispose();
      this.gpuAnalyzer = null;
    }

    if (this.thicknessCompositor) {
      this.thicknessCompositor.dispose();
      this.thicknessCompositor = null;
    }

    if (this.thicknessDataMaterial) {
      this.thicknessDataMaterial.dispose();
      this.thicknessDataMaterial = null;
    }

    if (this.directColorMaterial) {
      this.directColorMaterial.dispose();
      this.directColorMaterial = null;
    }

    this.orijinalMaterials.clear();
    this.aktifMeshler = [];

    this.subsList.forEach(element => {
      if (element && element.unsubscribe) {
        element.unsubscribe();
      }
    });

    this.haritaOlaylar.forEach((value: any, key: string) => {
      if (value && typeof value === 'function') {
        value();
      }
    });
    this.haritaOlaylar.clear();

    this.anaGosterim = null;
  }
}
